import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchClinicianById } from "../../store/thunks/cliniciansThunk";
import { fetchAppointmentsByClinicianId } from "../../store/thunks/appointmentsThunk";
import {
  fetchClientsByClinicianId,
  fetchSupervisingClientsByClinicianId,
} from "../../store/thunks/clientsThunk";
import { fetchBlocksByClinicianId } from "../../store/thunks/blocksThunk";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import { setClient } from "../../store/slices/clientsSlice";

import Table from "../../applicationUi/components/Table";
import StatusLights from "../../applicationUi/components/StatusLights";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import ClientModal from "../../modals/ClientModal";
import ModifyBlockModal from "../../modals/ModifyBlockModal";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ClinicianDetailsModal({ clinicianId, onClose }) {
  const dispatch = useDispatch();

  // Consolidated loading state
  const [loadingState, setLoadingState] = useState({
    clinician: false,
    appointments: false,
    clients: false,
    supervisingClients: false,
    blocks: false,
  });

  // Data state
  const [data, setData] = useState({
    clinician: null,
    appointments: [],
    clients: [],
    supervisingClients: [],
    blocks: [],
  });

  // UI state
  const [currentTab, setCurrentTab] = useState("appointments");
  const [appointmentFilter, setAppointmentFilter] = useState("All");
  const [error, setError] = useState(null);

  // Modal states
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);

  // Search states
  const [searchQueries, setSearchQueries] = useState({
    appointments: "",
    clients: "",
    supervisingClients: "",
  });

  // Pagination & Sorting State
  const itemsPerPage = 10;
  const [sortConfigs, setSortConfigs] = useState({
    appointments: { key: "", direction: "ascending" },
    clients: { key: "", direction: "ascending" },
    supervisingClients: { key: "", direction: "ascending" },
    blocks: { key: "", direction: "ascending" },
  });

  const [currentPages, setCurrentPages] = useState({
    appointments: 1,
    clients: 1,
    supervisingClients: 1,
    blocks: 1,
  });

  // Data fetching with improved loading states and error handling
  const fetchClinicianData = useCallback(async () => {
    if (!clinicianId) return;

    console.log(`[Clinician Details Modal] Starting data fetch for clinician ${clinicianId}`);
    setError(null);

    try {
      // Step 1: Fetch clinician details
      console.log(`[Clinician Details Modal] Fetching clinician details...`);
      setLoadingState(prev => ({ ...prev, clinician: true }));
      
      const clinicianData = await dispatch(fetchClinicianById(clinicianId)).unwrap();
      console.log(`[Clinician Details Modal] Clinician fetched: ${clinicianData.firstName} ${clinicianData.lastName}`);
      
      setData(prev => ({ ...prev, clinician: clinicianData }));
      setLoadingState(prev => ({ ...prev, clinician: false }));

      // Step 2: Fetch all related data in parallel
      const fetchPromises = [
        {
          key: 'appointments',
          promise: dispatch(fetchAppointmentsByClinicianId(clinicianData._id)).unwrap(),
          message: 'Fetching appointments...'
        },
        {
          key: 'clients', 
          promise: dispatch(fetchClientsByClinicianId(clinicianData._id)).unwrap(),
          message: 'Fetching clients...'
        },
        {
          key: 'supervisingClients',
          promise: dispatch(fetchSupervisingClientsByClinicianId(clinicianData._id)).unwrap(),
          message: 'Fetching supervising clients...'
        },
        {
          key: 'blocks',
          promise: dispatch(fetchBlocksByClinicianId(clinicianData._id)).unwrap(),
          message: 'Fetching blocks...'
        }
      ];

      // Set all to loading
      setLoadingState(prev => ({
        ...prev,
        appointments: true,
        clients: true,
        supervisingClients: true,
        blocks: true,
      }));

      // Execute all fetches in parallel
      const results = await Promise.allSettled(fetchPromises.map(({ promise, key, message }) => {
        console.log(`[Clinician Details Modal] ${message}`);
        return promise.then(result => ({ key, result }));
      }));

      // Process results
      const newData = { clinician: clinicianData, appointments: [], clients: [], supervisingClients: [], blocks: [] };
      const newLoadingState = { clinician: false, appointments: false, clients: false, supervisingClients: false, blocks: false };

      results.forEach((result, index) => {
        const { key } = fetchPromises[index];
        
        if (result.status === 'fulfilled') {
          const fetchResult = result.value;
          newData[fetchResult.key] = Array.isArray(fetchResult.result) ? fetchResult.result : [];
          console.log(`[Clinician Details Modal] ${fetchPromises[index].message.replace('Fetching', 'Fetched')} - ${newData[fetchResult.key].length} items`);
        } else {
          console.error(`[Clinician Details Modal] Failed to fetch ${key}:`, result.reason);
          newData[key] = [];
        }
      });

      setData(newData);
      setLoadingState(newLoadingState);
      console.log(`[Clinician Details Modal] All data fetched successfully`);

    } catch (error) {
      console.error('[Clinician Details Modal] Error fetching clinician data:', error);
      setError(`Failed to load clinician data: ${error.message}`);
      setLoadingState({ clinician: false, appointments: false, clients: false, supervisingClients: false, blocks: false });
    }
  }, [clinicianId, dispatch]);

  useEffect(() => {
    fetchClinicianData();
  }, [fetchClinicianData]);

  // Memoized filtered data functions for better performance
  const filteredData = useMemo(() => {
    const filterBySearch = (items, searchQuery, searchFields) => {
      if (!searchQuery) return items;
      const query = searchQuery.toLowerCase();
      return items.filter(item => 
        searchFields.some(field => {
          const value = field.includes('.') ? 
            field.split('.').reduce((obj, key) => obj?.[key], item) : 
            item[field];
          return value && value.toString().toLowerCase().includes(query);
        })
      );
    };

    // Filter appointments
    const appointmentsToFilter = data.appointments.filter(appointment => {
      const appointmentDate = new Date(appointment.start);
      const now = new Date();
      if (appointmentFilter === "Future") return appointmentDate >= now;
      if (appointmentFilter === "Past") return appointmentDate < now;
      return true;
    });

    const filteredAppointments = filterBySearch(
      appointmentsToFilter,
      searchQueries.appointments,
      ['title', 'service.description', 'client.firstName', 'client.lastName']
    ).map(appt => ({
      ...appt,
      clientName: appt.client?.firstName && appt.client?.lastName
        ? `${appt.client.firstName} ${appt.client.lastName}`
        : "Unknown"
    }));

    // Filter clients (excluding discharged and parents)
    const activeClients = data.clients.filter(
      client => client.clientStatus !== "discharged" && client.type !== "parent"
    );
    const filteredClients = filterBySearch(
      activeClients,
      searchQueries.clients,
      ['firstName', 'lastName', 'email']
    );

    // Filter supervising clients (excluding discharged and parents)
    const activeSupervisingClients = data.supervisingClients.filter(
      client => client.clientStatus !== "discharged" && client.type !== "parent"
    );
    const filteredSupervisingClients = filterBySearch(
      activeSupervisingClients,
      searchQueries.supervisingClients,
      ['firstName', 'lastName', 'email']
    );

    // Transform blocks
    const transformedBlocks = data.blocks.map(block => ({
      ...block,
      clientName: block.appointments?.length > 0
        ? `${block.appointments[0]?.client?.firstName || "Unknown"} ${
            block.appointments[0]?.client?.lastName || ""
          }`
        : "No Client",
      start: block.appointments?.length > 0 ? block.appointments[0].start : null,
      end: block.appointments?.length > 0 
        ? block.appointments[block.appointments.length - 1].start 
        : null,
      appointmentsCount: block.appointments?.length || 0,
    }));

    return {
      appointments: filteredAppointments,
      clients: filteredClients,
      supervisingClients: filteredSupervisingClients,
      blocks: transformedBlocks,
    };
  }, [data, searchQueries, appointmentFilter]);

  // Memoized sorted and paginated data for better performance
  const processedData = useMemo(() => {
    const getNestedValue = (obj, key) => {
      return key.split(".").reduce((o, i) => (o ? o[i] : undefined), obj);
    };

    const sortData = (items, sortConfig) => {
      if (!sortConfig.key) return items;
      return [...items].sort((a, b) => {
        let aVal = getNestedValue(a, sortConfig.key);
        let bVal = getNestedValue(b, sortConfig.key);

        // Handle date fields
        if (sortConfig.key === "start" || sortConfig.key === "end") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    };

    const paginateData = (items, currentPage) => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return items.slice(startIndex, startIndex + itemsPerPage);
    };

    // Process each data type
    const result = {};
    Object.keys(filteredData).forEach(key => {
      const sorted = sortData(filteredData[key], sortConfigs[key]);
      const paginated = paginateData(sorted, currentPages[key]);
      result[key] = {
        items: paginated,
        total: filteredData[key].length,
        totalPages: Math.ceil(filteredData[key].length / itemsPerPage),
      };
    });

    return result;
  }, [filteredData, sortConfigs, currentPages]);

  // Tab definitions with loading-aware counts
  const tabs = useMemo(() => {
    const activeClients = data.clients.filter(
      client => client.clientStatus !== "discharged" && client.type !== "parent"
    );
    const activeSupervisingClients = data.supervisingClients.filter(
      client => client.clientStatus !== "discharged" && client.type !== "parent"
    );

    return [
      { 
        name: `Appointments - ${loadingState.appointments ? '...' : data.appointments.length}`, 
        key: "appointments" 
      },
      {
        name: `Treating Clients - ${loadingState.clients ? '...' : activeClients.length}`,
        key: "clients",
      },
      {
        name: `Supervising Clients - ${loadingState.supervisingClients ? '...' : activeSupervisingClients.length}`,
        key: "supervising-clients",
      },
      { 
        name: `Blocks - ${loadingState.blocks ? '...' : data.blocks.length}`, 
        key: "blocks" 
      },
    ];
  }, [data, loadingState]);

  // Optimized handler functions with useCallback for better performance
  const handleSortChange = useCallback((dataType, key) => {
    setSortConfigs(prev => {
      const currentConfig = prev[dataType];
      const direction = currentConfig.key === key && currentConfig.direction === "ascending" 
        ? "descending" 
        : "ascending";
      return {
        ...prev,
        [dataType]: { key, direction }
      };
    });
  }, []);

  const handlePageChange = useCallback((dataType, page) => {
    setCurrentPages(prev => ({
      ...prev,
      [dataType]: page
    }));
  }, []);

  const handleSearchChange = useCallback((dataType, query) => {
    setSearchQueries(prev => ({
      ...prev,
      [dataType]: query
    }));
    // Reset to first page when searching
    setCurrentPages(prev => ({
      ...prev,
      [dataType]: 1
    }));
  }, []);

  // Row click handlers
  const handleAppointmentClick = useCallback((appointmentId) => {
    const appointment = data.appointments.find(appt => appt._id === appointmentId);
    if (appointment) {
      dispatch(setSelectedAppointment(appointment));
      setIsAppointmentSummaryOpen(true);
    }
  }, [data.appointments, dispatch]);

  const handleClientClick = useCallback((clientId) => {
    const client = data.clients.find(client => client._id === clientId) ||
                   data.supervisingClients.find(client => client._id === clientId);
    if (client) {
      dispatch(setClient(client));
      setIsClientModalOpen(true);
    }
  }, [data.clients, data.supervisingClients, dispatch]);

  const handleBlockClick = useCallback((blockId) => {
    setSelectedBlockId(blockId);
    setIsBlockModalOpen(true);
  }, []);

  // Loading spinner component
  const LoadingSpinner = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-3"></div>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );

  const handleCloseAppointmentSummary = () => {
    dispatch(setSelectedAppointment(null));
    setIsAppointmentSummaryOpen(false);
  };

  const handleCloseClientModal = () => {
    dispatch(setClient(null));
    setIsClientModalOpen(false);
  };

  const handleCloseBlockModal = () => {
    setSelectedBlockId(null);
    setIsBlockModalOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50">
      <div className="relative mx-4 my-8 w-full max-w-6xl rounded-sm bg-white border border-gray-200 shadow-xl">
        {/* Header & Close Button */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {loadingState.clinician ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                  Loading...
                </div>
              ) : (
                `${data.clinician?.firstName || ''} ${data.clinician?.lastName || ''}`
              )}
            </h3>
            <p className="text-sm text-gray-600 mt-1">Clinician Details</p>
            {error && (
              <p className="text-sm text-red-600 mt-1">{error}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-4">
          {/* Mobile Dropdown */}
          <div className="sm:hidden">
            <select
              value={currentTab}
              onChange={(e) => setCurrentTab(e.target.value)}
              className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.name}
                </option>
              ))}
            </select>
          </div>
          {/* Desktop Tabs */}
          <div className="hidden sm:block">
            <nav className="flex space-x-8 border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setCurrentTab(tab.key)}
                  className={classNames(
                    currentTab === tab.key
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
          {currentTab === "appointments" && (
            <div className="space-y-4">
              {loadingState.appointments ? (
                <LoadingSpinner message="Fetching appointments..." />
              ) : (
                <>
                  {/* Search and Filter Controls */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex-1">
                      <input
                        id="appointment-search"
                        type="text"
                        placeholder="Search appointments..."
                        value={searchQueries.appointments}
                        onChange={(e) => handleSearchChange('appointments', e.target.value)}
                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                    <div className="w-full sm:w-48">
                      <select
                        value={appointmentFilter}
                        onChange={(e) => setAppointmentFilter(e.target.value)}
                        className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      >
                        <option value="All">All Appointments</option>
                        <option value="Future">Future</option>
                        <option value="Past">Past</option>
                      </select>
                    </div>
                  </div>

                  <Table
                    title="Appointments"
                    description="List of appointments"
                    columns={[
                      { header: "Title", accessor: "title", sortable: true },
                      { header: "Client", accessor: "clientName", sortable: true },
                      { 
                        header: "Service Date", 
                        accessor: (row) => {
                          if (!row.start) return 'No date';
                          const date = new Date(row.start);
                          return date.toLocaleString('en-US', {
                            timeZone: 'America/New_York',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          });
                        }, 
                        sortable: true 
                      },
                      {
                        header: "Clinic",
                        accessor: (row) => row.clinic?.name || 'No clinic',
                        sortable: true,
                      },
                      {
                        header: "Status",
                        accessor: (row) => <StatusLights appointment={row} />,
                        sortable: false,
                      },
                    ]}
                    data={processedData.appointments?.items || []}
                    onRowClick={handleAppointmentClick}
                    showPagination={true}
                    currentPage={currentPages.appointments}
                    totalPages={processedData.appointments?.totalPages || 1}
                    onPageChange={(page) => handlePageChange('appointments', page)}
                    onSortChange={(key) => handleSortChange('appointments', key)}
                    totalResults={processedData.appointments?.total || 0}
                    sortConfig={sortConfigs.appointments}
                  />
                </>
              )}
            </div>
          )}

          {currentTab === "clients" && (
            <div className="space-y-4">
              {loadingState.clients ? (
                <LoadingSpinner message="Fetching clients..." />
              ) : (
                <>
                  <div>
                    <input
                      id="client-search"
                      type="text"
                      placeholder="Search treating clients..."
                      value={searchQueries.clients}
                      onChange={(e) => handleSearchChange('clients', e.target.value)}
                      className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  <Table
                    title="Treating Clients"
                    description="List of active treating clients"
                    columns={[
                      { header: "First Name", accessor: "firstName", sortable: true },
                      { header: "Last Name", accessor: "lastName", sortable: true },
                      { header: "Email", accessor: "email", sortable: true },
                      {
                        header: "Phone",
                        accessor: "phoneNumbers[0].number",
                        sortable: true,
                      },
                    ]}
                    data={processedData.clients?.items || []}
                    onRowClick={handleClientClick}
                    showPagination={true}
                    currentPage={currentPages.clients}
                    totalPages={processedData.clients?.totalPages || 1}
                    onPageChange={(page) => handlePageChange('clients', page)}
                    onSortChange={(key) => handleSortChange('clients', key)}
                    totalResults={processedData.clients?.total || 0}
                    sortConfig={sortConfigs.clients}
                  />
                </>
              )}
            </div>
          )}

          {currentTab === "supervising-clients" && (
            <div className="space-y-4">
              {loadingState.supervisingClients ? (
                <LoadingSpinner message="Fetching supervising clients..." />
              ) : (
                <>
                  <div>
                    <input
                      id="supervising-client-search"
                      type="text"
                      placeholder="Search supervising clients..."
                      value={searchQueries.supervisingClients}
                      onChange={(e) => handleSearchChange('supervisingClients', e.target.value)}
                      className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>

                  <Table
                    title="Supervising Clients"
                    description="List of active supervising clients"
                    columns={[
                      { header: "First Name", accessor: "firstName", sortable: true },
                      { header: "Last Name", accessor: "lastName", sortable: true },
                      { header: "Email", accessor: "email", sortable: true },
                      {
                        header: "Phone",
                        accessor: "phoneNumbers[0].number",
                        sortable: true,
                      },
                    ]}
                    data={processedData.supervisingClients?.items || []}
                    onRowClick={handleClientClick}
                    showPagination={true}
                    currentPage={currentPages.supervisingClients}
                    totalPages={processedData.supervisingClients?.totalPages || 1}
                    onPageChange={(page) => handlePageChange('supervisingClients', page)}
                    onSortChange={(key) => handleSortChange('supervisingClients', key)}
                    totalResults={processedData.supervisingClients?.total || 0}
                    sortConfig={sortConfigs.supervisingClients}
                  />
                </>
              )}
            </div>
          )}

          {currentTab === "blocks" && (
            <div>
              {loadingState.blocks ? (
                <LoadingSpinner message="Fetching blocks..." />
              ) : (
                <Table
                  title="Blocks"
                  description="List of blocks"
                  columns={[
                    { header: "Client", accessor: "clientName", sortable: false },
                    { header: "Starts On", accessor: "start", sortable: true },
                    { header: "Ends On", accessor: "end", sortable: true },
                    {
                      header: "Appointments",
                      accessor: "appointmentsCount",
                      sortable: true,
                    },
                  ]}
                  data={processedData.blocks?.items || []}
                  onRowClick={handleBlockClick}
                  showPagination={true}
                  currentPage={currentPages.blocks}
                  totalPages={processedData.blocks?.totalPages || 1}
                  onPageChange={(page) => handlePageChange('blocks', page)}
                  onSortChange={(key) => handleSortChange('blocks', key)}
                  totalResults={processedData.blocks?.total || 0}
                  sortConfig={sortConfigs.blocks}
                />
              )}
            </div>
          )}
        </div>

        {isAppointmentSummaryOpen && (
          <AppointmentSummary
            open={isAppointmentSummaryOpen}
            onClose={handleCloseAppointmentSummary}
            onReschedule={(appt) => console.log("Reschedule:", appt)}
          />
        )}

        {isClientModalOpen && (
          <ClientModal
            isOpen={isClientModalOpen}
            onClose={handleCloseClientModal}
          />
        )}

        {isBlockModalOpen && (
          <ModifyBlockModal
            isOpen={isBlockModalOpen}
            onClose={handleCloseBlockModal}
            blockId={selectedBlockId}
          />
        )}
      </div>
    </div>
  );
}